import type { LayoutShell } from './layoutShell'

/**
 * The layout shell's right zone (T5). Two occupants share it — the Details of
 * a clicked feature (T2) and the Data Filter form — so neither may own the
 * zone the way `detailsPanel` used to. This module owns it: it holds the tab
 * strip, the active tab and the badge, and hands each occupant an empty pane.
 *
 * The distinction that matters is `open()` vs `present()`. `open()` is a
 * deliberate user action and always brings its tab up. `present()` is content
 * arriving on its own (a map click): it must never yank the user off a
 * half-filled form, so it badges instead of switching.
 */
export interface RightPanel {
  /** Registers a tab and returns its (initially hidden) pane to fill. */
  addTab(id: string, label: string): HTMLElement
  /** The user asked for this tab: activate it and reveal the panel. */
  open(id: string): void
  /** Content arrived by itself: come up only if not standing on another tab. */
  present(id: string): void
  /** Collapse the whole panel. */
  close(): void
  /** Notified when the panel is dismissed, whatever tab was up. */
  onClose(cb: () => void): void
}

export function createRightPanel(shell: LayoutShell): RightPanel {
  const root = shell.right
  root.classList.add('right-panel')
  root.innerHTML = `
    <div class="right-panel__head">
      <div class="right-panel__tabs" role="tablist"></div>
      <button type="button" class="right-panel__close" aria-label="Close">×</button>
    </div>
    <div class="right-panel__body"></div>`
  const tabsEl = root.querySelector('.right-panel__tabs') as HTMLElement
  const bodyEl = root.querySelector('.right-panel__body') as HTMLElement

  interface Tab {
    button: HTMLButtonElement
    pane: HTMLElement
    badge: HTMLElement
  }
  const tabs = new Map<string, Tab>()
  const closeHandlers: Array<() => void> = []
  let active: string | undefined

  root.querySelector('.right-panel__close')!.addEventListener('click', () => {
    shell.closeRight()
    for (const cb of closeHandlers) cb()
  })

  function activate(id: string): void {
    active = id
    for (const [key, tab] of tabs) {
      tab.pane.hidden = key !== id
      tab.button.setAttribute('aria-selected', String(key === id))
      // Visiting a tab is what clears its badge.
      if (key === id) tab.badge.hidden = true
    }
  }

  return {
    addTab(id, label) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'right-panel__tab'
      button.dataset.tab = id
      button.setAttribute('role', 'tab')
      button.setAttribute('aria-selected', 'false')
      button.textContent = label
      const badge = document.createElement('span')
      badge.className = 'right-panel__badge'
      badge.setAttribute('aria-label', 'New content')
      badge.hidden = true
      button.appendChild(badge)
      button.addEventListener('click', () => activate(id))
      tabsEl.appendChild(button)

      const pane = document.createElement('section')
      pane.className = 'right-panel__pane'
      pane.dataset.tab = id
      pane.hidden = true
      bodyEl.appendChild(pane)

      tabs.set(id, { button, pane, badge })
      // A lone tab is a title, not a choice: nothing to switch between, so it
      // must not look clickable. Two or more turn the strip into real tabs.
      tabsEl.classList.toggle('right-panel__tabs--single', tabs.size < 2)
      if (tabs.size === 1) activate(id)
      return pane
    },
    open(id) {
      activate(id)
      shell.openRight()
    },
    present(id) {
      const panelClosed = root.hidden
      if (!panelClosed && active && active !== id) {
        tabs.get(id)!.badge.hidden = false
        return
      }
      activate(id)
      shell.openRight()
    },
    close() {
      shell.closeRight()
    },
    onClose(cb) {
      closeHandlers.push(cb)
    },
  }
}
